import React, { useEffect, useState } from 'react';
import { TableDefaultSticky } from '../../../../shared/components/table/TableDefaultSticky';
import { TableColumnType } from '../../../../shared/components/table/table-types';
import { useCallAction, useEvent, useStore } from '@cobuildlab/react-simple-state';
import { Box, Card, CardContent, Typography } from '@mui/material';
import { subscriptionStatus } from '../../dashboard-utils';
import { fetchSubscriptionsNextExpireByMonthEvent } from '../../../subscription/subscription-events';
import { fetchSubscriptionsNextExpireByMonth } from '../../../subscription/subscription-actions';
import { GlobalSubscriptionTable } from '../../../subscription/subscription-types';
import { subscriptionNextExpireTableFilterStore } from '../../../subscription/subscription-store';
import { useStylesCustom } from '../DashboardCardItem';
import { useTableSort } from '../../../../shared/components/table/useTableSort';
import { SubscriptionUserModal } from './SubscriptionUserModal';
import { getDateByTimezone } from '../../../../shared/utils';
import { SubscriptionNextExpireTableFilter } from '../../../subscription/components/SubscriptionNextExpireTableFilter';
import { DEFAULT_TIMEZONE } from '../../../../shared/constants';
import { useSubscriptionsNextExpireReportMutation } from '../../../../shared/types/generated';
import { openSnackbarAction } from '../../../../shared/components/snackbar/snackbar-actions';
import { TypeMessage } from '../../../../shared/components/snackbar/snackbar-types';
import { ButtonDefault } from '../../../../shared/components/ButtonDefault';
import { PRIMARY_COLOR } from '../../../../shared/css/theme';
import { useAuth } from '../../../auth/auth-hook';

export const DashboardSubscriptionsNextExpiredTable: React.FC = () => {
  const { user } = useAuth();
  const classes = useStylesCustom();
  const [openModal, setOpenModal] = useState(false);
  const [currentUserSubscription, setCurrentUserSubscription] = useState<GlobalSubscriptionTable>();
  const { data, total, paymentMethods } = useEvent(fetchSubscriptionsNextExpireByMonthEvent);

  const [callReport, { loading: loadingReport }] = useSubscriptionsNextExpireReportMutation({
    onCompleted: () => {
      openSnackbarAction('Su reporte estara disponible en unos minutos', TypeMessage.SUCCESS);
    },
    onError: (error) => {
      console.error('Error generating report', error);
      openSnackbarAction('Error al generar el reporte', TypeMessage.ERROR);
    },
  });

  const [callFetchSubscriptionsNextExpire, loading] = useCallAction(
    fetchSubscriptionsNextExpireByMonth,
  );

  const { monthExpire, status, paymentMethod } = useStore(subscriptionNextExpireTableFilterStore);
  const { sort, handleSort } = useTableSort();

  const [pagination, setPagination] = useState({
    pageSize: 10,
    page: 1,
  });

  useEffect(() => {
    callFetchSubscriptionsNextExpire({
      page: pagination.page - 1,
      monthExpire,
      status,
      paymentMethod,
      orderBy: sort,
    });
  }, [callFetchSubscriptionsNextExpire, pagination.page, monthExpire, status, paymentMethod, sort]);

  const handleOpenModal = (item: GlobalSubscriptionTable): void => {
    setCurrentUserSubscription(item);
    setOpenModal(true);
  };

  const COLUMNS: TableColumnType<GlobalSubscriptionTable>[] = [
    {
      columnName: 'ID',
      columnValue: (item) => item.id,
      sortableField: 'id',
    },
    {
      columnName: 'NOMBRE',
      columnValue: null,

      columnComponent: (item) => <Box textAlign="left">{item.user_name}</Box>,
      sortableField: 'user_name',
    },
    {
      columnName: 'EMAIL',
      columnValue: null,

      columnComponent: (item) => <Box textAlign="left">{item.user_email as string}</Box>,
      sortableField: 'user_email',
    },
    {
      columnName: 'PLAN',
      columnValue: null,

      columnComponent: (item) => <Box textAlign="left">{item.plan_title as string}</Box>,
      sortableField: 'plan_title',
    },
    {
      columnName: 'N° DE SUSCRIPCIÓN',
      columnValue: null,

      columnComponent: (item) => <Box textAlign="left">{item.transaction_id as string}</Box>,
      sortableField: 'transaction_id',
    },
    {
      columnName: 'FECHA DE SUSCRIPCIÓN',
      columnValue: null,
      columnComponent: (item) => (
        <Box>{getDateByTimezone(item.created_at as string, DEFAULT_TIMEZONE)}</Box>
      ),
      sortableField: 'period_start_date',
    },
    {
      columnName: 'FECHA DE VENCIMIENTO',
      columnValue: null,

      columnComponent: (item) => (
        <Box>{getDateByTimezone(item.period_end_date as string, DEFAULT_TIMEZONE)}</Box>
      ),
      sortableField: 'period_end_date',
    },
    {
      columnName: 'MÉTODO DE PAGO',
      columnValue: null,

      columnComponent: (item) => <Box textAlign="left">{item.payment_method as string}</Box>,
      sortableField: 'payment_method',
    },
    {
      columnName: 'STATUS',
      columnValue: null,
      columnComponent: (item) => <Box textAlign="left">{subscriptionStatus(item.status)}</Box>,
      sortableField: 'status',
    },
  ];

  return (
    <>
      <SubscriptionUserModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        userSubscription={currentUserSubscription}
      />
      <Box
        marginY={2}
        sx={{
          display: { xs: 'block', md: 'flex' },
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          component={'div'}
          sx={{ marginBottom: { xs: '10px', md: '0px' } }}
          fontSize={20}
          fontWeight={700}
        >
          Suscripciones próximas a renovar
        </Typography>
        <Box>
          <ButtonDefault
            onClick={() => {
              callReport({
                variables: {
                  data: {
                    monthExpire,
                    status: status,
                    payment_method: paymentMethod,
                    requested_by: user?.name || user?.email || '',
                  },
                },
              });
            }}
            startIcon={
              !loadingReport ? <img src="/icon-clip.svg" alt="clip" width={14} height={14} /> : null
            }
            isLoading={loadingReport}
            style={{
              width: 195,
              height: 40,
              textTransform: 'inherit',
              fontWeight: 700,
              background: PRIMARY_COLOR ?? 'black',
            }}
          >
            Descargar reporte
          </ButtonDefault>
          <SubscriptionNextExpireTableFilter paymentMethodFilters={paymentMethods || []} />
        </Box>
      </Box>
      <Card
        elevation={2}
        style={{
          border: '1px solid #BBBBBB',
          padding: '16px',
          borderRadius: '16px',
          boxShadow: '0px 0px 8px 0px #00000014',
        }}
      >
        <CardContent className={classes.content}>
          <TableDefaultSticky
            count={total}
            items={data}
            columns={COLUMNS}
            page={pagination.page}
            pageSize={pagination.pageSize}
            loading={loading}
            messageLoading={'Cargando suscripciones proximas a vencer...'}
            handleClickRow={(item) => handleOpenModal(item)}
            handleChangePage={(page) => {
              setPagination({
                ...pagination,
                page: page + 1,
              });
            }}
            handleSort={handleSort}
          />
        </CardContent>
      </Card>
    </>
  );
};
